import { dateFormat, getCurrency, getNumberFormat } from "../../../../utils/helper";
import { IN_REPAIR_PHASE_AWAITING_CUSTOMER } from "../data/inRepairListingConstants";
import {
  RO_DETAIL_COLORS,
  RO_DETAIL_DEFAULT_BACK_LABEL,
  RO_DETAIL_ECON_FLAG_COLORS,
  RO_DETAIL_PAGE_BACK_LABELS,
  RO_DETAIL_QUERY_ID,
  RO_DETAIL_QUERY_OPEN,
  RO_DETAIL_QUERY_RO,
  RO_DETAIL_RAG_PILL_STYLE,
  RO_DETAIL_RAG_SLA_FALLBACK_PCT,
} from "../data/inRepairRoDetailConstants";

export const isRoDetailDeepLink = (location) => {
  const pathname = location?.pathname || "";
  if (!pathname.includes("repair-orders")) {
    return false;
  }

  const params = new URLSearchParams(location?.search || "");
  const openRo = params.get(RO_DETAIL_QUERY_OPEN);
  const ro = params.get(RO_DETAIL_QUERY_RO);
  const id = params.get(RO_DETAIL_QUERY_ID);

  return (
    openRo != null && openRo !== "" && Boolean(ro) && Boolean(id)
  );
};

export const getRoDetailBackLabel = (pathname = "") => {
  const match = String(pathname).match(/repair-orders\/([^/?]+)/);
  const segment = match?.[1];
  return RO_DETAIL_PAGE_BACK_LABELS[segment] ?? RO_DETAIL_DEFAULT_BACK_LABEL;
};

export const fmtRoDetailDate = (value, fallback = "\u2014") =>
  value ? dateFormat(value) : fallback;

export const fmtRoDetailDays = (value, suffix = "days") => {
  if (value === null || value === undefined || value === "") return "\u2014";
  const n = Number(value);
  if (!Number.isFinite(n)) return "\u2014";
  return `${n} ${suffix}`;
};

export const fmtRoDetailCurrency = (value) =>
  value || value === 0 ? getCurrency(value) : "\u2014";

export const fmtRoDetailNumber = (value) => {
  if (value === null || value === undefined || value === "") return "\u2014";
  return getNumberFormat(value);
};

export const fmtRoDetailTatDays = (value) => {
  if (value === null || value === undefined || value === "") return "\u2014";
  const n = Number(value);
  if (!Number.isFinite(n)) return "\u2014";
  return `${n} d`;
};

export const fmtRoDetailDaysToEdd = (value) => {
  if (value === null || value === undefined || value === "") return "\u2014";
  const n = Number(value);
  if (!Number.isFinite(n)) return "\u2014";
  const days = Math.abs(n);
  return n > 0 ? `${days} days remaining` : `${days} days overdue`;
};

export const fmtRoDetailPercent = (value) =>
  value || value === 0 ? `${(value * 100).toFixed(1)}%` : "\u2014";

export const getRoDetailEconFlagColor = (flag) => {
  if (!flag) return RO_DETAIL_ECON_FLAG_COLORS.default;
  if (flag.includes("High")) return RO_DETAIL_ECON_FLAG_COLORS.high;
  if (flag.includes("Review")) return RO_DETAIL_ECON_FLAG_COLORS.review;
  return RO_DETAIL_ECON_FLAG_COLORS.positive;
};

export const getRoDetailStepperStyle = (status, phase) => {
  const isAwaitingCustomer = phase === IN_REPAIR_PHASE_AWAITING_CUSTOMER;

  if (status === "completed") {
    return {
      bg: RO_DETAIL_COLORS.stepperCompletedBg,
      color: RO_DETAIL_COLORS.stepperCompletedText,
    };
  }
  if (status === "active" && isAwaitingCustomer) {
    return {
      bg: RO_DETAIL_COLORS.stepperAwaitingCustomerBg,
      color: RO_DETAIL_COLORS.stepperAwaitingCustomerText,
    };
  }
  if (status === "active") {
    return { bg: RO_DETAIL_COLORS.activeStepperBg, color: "#ffffff" };
  }
  return {
    bg: RO_DETAIL_COLORS.stepperPendingBg,
    color: RO_DETAIL_COLORS.stepperPendingText,
  };
};

export const getRoDetailRagPillStyle = (ragColor) =>
  RO_DETAIL_RAG_PILL_STYLE[ragColor] || RO_DETAIL_RAG_PILL_STYLE.Gray;

export const getRoDetailSlaBarColor = (ragColor) =>
  getRoDetailRagPillStyle(ragColor).bg;

export const getRoDetailSlaBarPct = (phaseSla) =>
  phaseSla?.progress_pct ??
  RO_DETAIL_RAG_SLA_FALLBACK_PCT[phaseSla?.rag_color] ??
  40;

export const buildRoDetailSubtitle = (data) =>
  [
    data?.description,
    data?.part_number && `P/N: ${data.part_number}`,
    data?.serial_number && `S/N: ${data.serial_number}`,
  ]
    .filter(Boolean)
    .join(" \u00b7 ");

export const getRoDetailSubtitleParts = (data) => ({
  description: data?.description || "",
  partNumber: data?.part_number || "",
  serialNumber: data?.serial_number || "",
});

const hasRoDetailDateValue = (value) => value != null && value !== "";

const hasRoDetailDaysValue = (value) =>
  value != null && value !== "" && Number.isFinite(Number(value));

export const getRoDetailPhaseHistoryLines = (phase = {}) => {
  const startDate = phase.start_date ?? phase.startDate;
  const endDate = phase.end_date ?? phase.endDate;
  const daysInPhase = phase.days_in_phase ?? phase.daysInPhase;
  const slaDays = phase.sla_days ?? phase.slaDays;

  const dateParts = [];
  if (hasRoDetailDateValue(startDate)) {
    dateParts.push(`Start: ${fmtRoDetailDate(startDate)}`);
  }
  if (hasRoDetailDateValue(endDate)) {
    dateParts.push(`End: ${fmtRoDetailDate(endDate)}`);
  }

  const hasDaysInPhase = hasRoDetailDaysValue(daysInPhase);
  const hasSlaDays = hasRoDetailDaysValue(slaDays);
  const daysParts = [];

  if (hasDaysInPhase) {
    daysParts.push(`Days in phase: ${Number(daysInPhase)}`);
  }
  if (hasSlaDays) {
    daysParts.push(`SLA: ${Number(slaDays)} days`);
  }

  return {
    datesLine: dateParts.length ? dateParts.join(" \u00b7 ") : null,
    daysLine: daysParts.length ? daysParts.join(" \u00b7 ") : null,
    isDaysOverSla:
      hasDaysInPhase &&
      hasSlaDays &&
      Number(daysInPhase) > Number(slaDays),
  };
};
